import { useEffect, useState } from "react";
import { DatePicker, Input, Modal, Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { assembleStatusTitles } from "../../../utils";
import locale from "antd/es/date-picker/locale/pt_BR";
import { UseQueryResult, useQueries } from "react-query";
import { api } from "../../../config";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../interfaces/users";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { EventAssembleInterface } from "../../../interfaces/eventAssemble";
import { useModal } from "../../../utils/hooks/useModal";
import TicketDrawer from "../../Shared/Components/TicketDrawer";
import { printSchedule } from "../../AssemblyCalendar/AssemblyCalendar";
import RescheduleAssemblyEvent from "../../AssemblyCalendar/RescheduleAssemblyEvent/RescheduleAssemblyEvent";

interface DataType {
  key: string;
  date: string;
  tags: string[];
  icon: object;
}

const defaultForm = {
  ownerId: "",
  title: "",
  dueDate: "",
};

function MainInformation({ ticket, disabled }: any) {
  const [edit, setEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const { onClose, onOpen, isOpen } = useModal()
  

  const [
    { data: usersData, isFetching: usersIsFetching },
    { refetch: ticketRefetch },
    { refetch: boardRefetch },
  ] = useQueries<UseQueryResult[]>([
    {
      queryKey: "users",
      queryFn: () => api(token).get(`/users?moduleName=ASSEMBLE_APP`),
    },
    {
      queryKey: "tickets/assemble",
      queryFn: () => api(token).get(`ticket/assemble/${ticket.id}`),
      refetchOnMount: false,
    },
    {
      queryKey: "tickets/assemble/board",
      queryFn: () => api(token).get(`tickets/assemble/board`),
      refetchOnMount: false,
    },
  ]);

  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    setFormData({
      ownerId: ticket.ownerId,
      title: ticket.title,
      dueDate: ticket.dueDate,
    });
  }, [ticket]);

  const handleOnFormChange = ({ target }: any) => {
    const { id, value } = target;
    setFormData({ ...formData, [id]: value });
  };

  const persistMeasureTicketInformation = async (
    token: string,
    ticketId: string,
    payload: any
  ) => {
    try {
      const updatedTicket = await api(token).put(
        `/ticket/assemble/${ticketId}`,
        payload
      );
      toast.success("Ticket atualizado com sucesso!");
      return updatedTicket;
    } catch (error) {
      toast.error("Ocorreu um erro durante a atualização do ticket");
    }
  };

  const saveDetails = async () => {
    setLoading(true);
    await persistMeasureTicketInformation(token, ticket.id, formData);
    ticketRefetch();
    boardRefetch();
    setEdit(false);
    setLoading(false);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Responsável",
      dataIndex: "name",
      key: "name",
      render: () => (
        <Select
          placeholder="Selecionar"
          disabled={!edit || usersIsFetching}
          style={{ width: 200 }}
          onChange={(e) =>
            handleOnFormChange({ target: { id: "ownerId", value: e } })
          }
          id="ownerId"
          value={formData.ownerId}
          options={(usersData as any[])?.map((item: any) => {
            return {
              value: item.id,
              label: item.fullName,
            };
          })}
        />
      ),
    },
    {
      title: "Título",
      dataIndex: "title",
      key: "title",
      render: (title) => (
        <Input
          value={formData.title}
          id="title"
          placeholder={title}
          disabled={!edit}
          onChange={handleOnFormChange}
        />
      ),
    },
    {
      title: "Data Limite",
      dataIndex: "deadline",
      key: "deadline",
      render: (date) => (
        <DatePicker
          id="dueDate"
          locale={locale}
          value={formData.dueDate ? dayjs(new Date(formData.dueDate)) : null}
          format="DD-MM-YYYY"
          disabled={!edit}
          showMinute={false}
          onChange={(value) =>
            handleOnFormChange({ target: { id: "dueDate", value } })
          }
          placeholder={date}
        />
      ),
    },
    {
      title: "Data do Agendamento",
      dataIndex: "event",
      key: "date",
      render: (event: EventAssembleInterface & { date: string }) => (
        <div
          className="cursor-pointer"
          onClick={() => {
            setEvent({
              assembler_id: event.assemblerId,
              start_time: event.start,
              end_time: event.end,
              id: event.id,
              seniority_id: 0
            });
            onOpen();
          }}
        >
          {event.date}
        </div>
      ),
    },
    {
      title: "ID Ticket",
      key: "externalId",
      dataIndex: "externalId",
      render: () => ticket.externalId,
    },
    {
      title: "ID Simp",
      key: "id",
      dataIndex: "id",
      render: () => ticket.id,
    },
    {
      title: "Situação",
      key: "tags",
      dataIndex: "tags",
      render: (_, { tags }) => (
        <>
          {tags.map((tag) => {
            let color = "blue";
            if (tag === "loser") {
              //TODO: CONDIÇÃO PARA TAG VERMELHA QUANDO TICKET CANCELADO.
              color = "volcano";
            }
            return (
              <Tag color={color} key={tag}>
                {tag?.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: "",
      dataIndex: "icon",
      key: "icon",
      render: (icon) => icon,
    },
  ];

    const cancelSchedule = (eventId: number) => {
    if (!event) return;
    if (ticket?.id) {
      api(token)
        .put(`ticket/assemble/${ticket.id}`, {
          status: "ASSEMBLE_PENDING_SCHEDULE",
        })
        .catch((error) => {
          toast.error("Ocorreu um erro ao cancelar o agendamento");
          return;
        });
    }
    api(token)
      .delete(`events/assemble/${eventId}`)
      .catch((error) => {
        console.log(error);
      })
      .then(() => {
        ticketRefetch()
        toast.success("Agendamento cancelado com sucesso!");
        onCloseReschedule();
        onClose();
      })
      .catch((error) => {
        console.log(error);
        toast.error("Ocorreu um erro ao cancelar o agendamento");
      });
  };

  const {isOpen: isOpenReschedule, onOpen: onOpenReschedule, onClose: onCloseReschedule} = useModal()
  const [event, setEvent] = useState<{
        start_time?: Date,
    end_time?: Date,
    assembler_id?: number,
    id?: number,
    seniority_id?: number,
  }>({
    start_time: undefined,
    end_time: undefined,
    assembler_id: undefined,
    id: undefined,
    seniority_id: undefined,
  })
  return (
    <>
        <Modal
          title="Reagendar"
          open={isOpenReschedule}
          onCancel={onCloseReschedule}
          footer={null}
        >
          <RescheduleAssemblyEvent
            event={event}
            onCancel={onCloseReschedule}
            onSuccess={() => {
              ticketRefetch()
              onCloseReschedule()
            }}
          />
        </Modal>
          <TicketDrawer
            ticketId={ticket.id}
            open={isOpen}
            buttons={[
              {
                text: "Reagendar",
                onClick: () => {
                  onOpenReschedule();
                },
              },
              {
                text: "Cancelar Agendamento",
                danger: true,
                onClick: () => {
                  cancelSchedule(event.id ?? 0);
                },
              },
              {
                text: "Imprimir Agenda",
                onClick: async () => {
                  setLoading(true)
                  await printSchedule(ticket.id, token);
                  setLoading(false)
                },
              },
            ]}
            onClose={onClose}
          />
        {/* )} */}
      <Table
        columns={columns}
        dataSource={ticket.EventsAssemble.map(
          (item: EventAssembleInterface) => ({
            key: item.id,
            event: {...item, date:format(new Date(item.start), "dd/MM/yyyy")},

            tags: [assembleStatusTitles[ticket?.status]],
            icon: [
              <>
                {/* <Button
                  type="link"
                  size={"middle"}
                  style={{ width: 32 }}
                  icon={edit ? <SaveTwoTone /> : <EditTwoTone />}
                  disabled={isSharedComponentDisabled(
                    "assemble",
                    user,
                    disabled
                  )}
                  className="p-1"
                  loading={loading}
                  onClick={() => (edit ? saveDetails() : setEdit(true))}
                /> */}
              </>,
            ],
          })
        )}
        pagination={false}
        className="mb-3"
      />
    </>
  );
}

export default MainInformation;
